import { FormGroup, IFormGroupProps, Intent, Button, Position } from '@blueprintjs/core'
import * as React from 'react'
import * as Sentry from '@sentry/browser'
import createFilterOptions from 'react-select-fast-filter-options'
import VirtualizedSelect from 'react-virtualized-select'
import Client from '../../client'
import { GutterTooltipError } from '../../routes/products_v5/components/JoinedForm/GutterTooltipError'
import {
  isRequiredError,
  renderError,
  renderErrorExceptRequired
} from '../../routes/products_v5/components/JoinedForm/Helpers'
import { usePossiblyStaleValue } from '../../hooks/usePossiblyStaleValue'
import CHANNELS from '../../constants/Channels'

interface CategoryMarketCategoryPickerV2Props extends IFormGroupProps {
  error?: string
  value: number
  marketId: number
  placeholder?: string
  simpleValue?: boolean
  showRequiredLabel?: boolean
  onChange: Function
  errorTooltipOffset?: number
}

const CategoryMarketCategoryPickerV2: React.FunctionComponent<CategoryMarketCategoryPickerV2Props> = props => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [categories, setCategories] = React.useState([])
  const formError = props.error

  // stale, so that fetchCategories won't fire each time the user chooses a new value
  const initialValue = usePossiblyStaleValue(props.value)

  const fetchCategories = React.useCallback(
    async (marketId: number) => {
      setLoading(true)
      setError(null)
      try {
        const res = await Client.categoryMarketCategories({ marketId })

        let { data } = res.data

        // Rolling this out for ebay will be more complicated
        if (props.marketId === CHANNELS.GOODWILLFINDS.id || props.marketId === CHANNELS.ETSY.id) {
          // We only want to show non-deprecated CMCs for users to choose,
          // but still show the current value (even if deprecated), so the form makes sense.
          // Always show non-leafs. In at least one case (CMC 10848) we have a non-leaf CMC
          // that's marked as deprecated by one of its child categories. We need to show parent so we can show the child categories.
          // removed cmc.isLeaf check because it shows nonLeaf duplicate categories
          data = data.filter(cmc => cmc.deprecatedById === null || cmc.id === initialValue)
        }
        data = data.map(c => ({ label: c.pathCache, value: c.id }))
        setCategories(data)
      } catch (e) {
        Sentry.captureException(e)
        setError(true)
        setCategories([])
      } finally {
        setLoading(false)
      }
    },
    [initialValue, props.marketId]
  )

  React.useEffect(() => {
    fetchCategories(props.marketId)
  }, [fetchCategories, props.marketId])

  const filterOptions = React.useMemo(() => createFilterOptions({ options: categories }), [categories])
  const labelInfo = props.showRequiredLabel ? 'Required' : null

  return (
    <FormGroup // ProductFormGroup is tricky to use here
      style={{ maxWidth: 720, flex: 1 }}
      helperText={
        renderErrorExceptRequired(formError, labelInfo) ||
        (error && (
          <Button onClick={() => fetchCategories(props.marketId)} minimal intent={Intent.DANGER} small>
            There was an error loading categories, click to try again.
          </Button>
        ))
      }
      className="float-right-formgroup-label"
      labelInfo={isRequiredError(formError) ? renderError(labelInfo) : labelInfo}
      {...props}
    >
      <GutterTooltipError isOpen={!!formError} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <VirtualizedSelect
          disabled={props.disabled}
          className="long-select"
          onChange={props.onChange}
          value={props.value}
          isLoading={loading}
          labelKey="label"
          valueKey="value"
          placeholder={props.placeholder}
          simpleValue={props.simpleValue === undefined ? true : props.simpleValue}
          clearable
          filterOptions={typeof filterOptions === 'function' ? filterOptions : createFilterOptions({ options: [] })}
          options={categories}
        />
      </GutterTooltipError>
    </FormGroup>
  )
}

export default React.memo(CategoryMarketCategoryPickerV2)
